import { useEffect, useMemo, useState } from "react";
import { fetchCocktails } from "@/lib/api";
import { Search, Star, X, Heart, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useSavedMatches } from "@/hooks/useSavedMatches";
import { printCocktail } from "@/lib/printCocktail";

const SPIRITS = ["All", "Whisky", "Vodka", "Rum", "Gin", "Tequila", "Wine"];
const TASTES = ["All", "Sweet", "Sour", "Fruity", "Bitter", "Smoky", "Refreshing"];

export default function Cocktails() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [spirit, setSpirit] = useState("All");
  const [taste, setTaste] = useState("All");
  const [beginner, setBeginner] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCocktails().then(setItems).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return items.filter(c => {
      if (spirit !== "All" && c.base_spirit !== spirit) return false;
      if (taste !== "All" && !c.taste_profile.includes(taste)) return false;
      if (beginner && c.beginner_rating < 4) return false;
      if (q && !c.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [items, q, spirit, taste, beginner]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="cocktails-page">
      <div className="max-w-3xl">
        <p className="eyebrow">The Database</p>
        <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl md:text-6xl font-bold text-cream leading-[1.05]">
          Twenty classics,<br /><span className="italic font-serif-editor text-pink-brand">no gatekeeping.</span>
        </h1>
        <p className="mt-4 text-cream/70 font-body max-w-2xl">
          From the crowd-pleasers to the connoisseur picks — every recipe explained like a friend would. Filter, learn, mix.
        </p>
      </div>

      {/* Filters */}
      <div className="mt-10 space-y-5">
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-cream/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search cocktails…"
            className="w-full h-12 pl-11 pr-11 rounded-full bg-ink-panel border border-cream/10 focus:outline-none focus:border-pink-brand text-cream font-body"
            data-testid="cocktail-search"
          />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-cream/40 hover:text-cream" data-testid="clear-search">
              <X size={16} />
            </button>
          )}
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <FilterPills label="Base Spirit" value={spirit} setValue={setSpirit} options={SPIRITS} testPrefix="spirit-filter" />
          <FilterPills label="Taste" value={taste} setValue={setTaste} options={TASTES} testPrefix="taste-filter" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-ink-panel border border-cream/10 cursor-pointer" data-testid="beginner-toggle">
            <input type="checkbox" checked={beginner} onChange={(e) => setBeginner(e.target.checked)} className="accent-pink-500" />
            <span className="text-sm font-semibold text-cream">Beginner-friendly</span>
          </label>
          {(q || spirit !== "All" || taste !== "All" || beginner) && (
            <button
              onClick={() => { setQ(""); setSpirit("All"); setTaste("All"); setBeginner(false); }}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-xs font-semibold uppercase tracking-widest text-pink-brand border border-pink-brand/40 hover:bg-pink hover:text-cream hover:border-pink transition-colors"
              data-testid="reset-filters"
            >
              <X size={14} /> Reset Filters
            </button>
          )}
        </div>
      </div>

      <p className="mt-6 text-sm text-cream/50 font-body" data-testid="cocktail-count">{filtered.length} cocktail{filtered.length !== 1 ? "s" : ""}</p>

      {loading ? (
        <div className="mt-10 text-center text-cream/60">Pouring the list…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-14 text-center py-10 card-cream max-w-xl mx-auto px-6" data-testid="cocktails-empty-state">
          <p className="font-serif-display text-2xl text-cream">No cocktails match those filters.</p>
          <p className="mt-2 text-cream/60 text-sm">Try loosening a filter or reset them all.</p>
          <button
            onClick={() => { setQ(""); setSpirit("All"); setTaste("All"); setBeginner(false); }}
            className="btn-pill btn-pink mt-5"
            data-testid="empty-reset-filters"
          >
            Reset filters
          </button>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
          {filtered.map((c) => (
            <CocktailCard key={c.id} c={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterPills({ label, value, setValue, options, testPrefix }) {
  return (
    <div className="w-full">
      <p className="text-[10px] uppercase tracking-widest text-cream/50 mb-2 font-semibold">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map(o => (
          <button
            key={o}
            onClick={() => setValue(o)}
            className={`h-10 px-4 rounded-full text-xs sm:text-sm font-semibold border transition-colors ${
              value === o ? "bg-ink text-cream border-cream" : "bg-ink-panel text-cream border-cream/15 hover:border-cream/40"
            }`}
            data-testid={`${testPrefix}-${o.toLowerCase()}`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function CocktailCard({ c }) {
  const { isSaved, toggle } = useSavedMatches();
  const saved = isSaved(c.slug);
  return (
    <Dialog>
      <div className="relative">
        <DialogTrigger asChild>
          <button className="card-cream overflow-hidden text-left w-full" data-testid={`cocktail-card-${c.slug}`}>
            <div className="aspect-[4/3] relative overflow-hidden">
              <img src={c.image} alt={c.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              <div className="absolute top-3 left-3 chip chip-pink bg-pink text-cream border-transparent">{c.base_spirit}</div>
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-2">
                <p className="font-serif-display text-xl font-bold text-cream leading-tight">{c.name}</p>
                <span className="flex items-center gap-0.5 text-pink-brand text-xs font-semibold">
                  <Star size={12} fill="currentColor" /> {c.beginner_rating}/5
                </span>
              </div>
              <p className="text-xs text-cream/60 mt-1">{c.strength} · {c.glassware}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {c.taste_profile.slice(0, 2).map(t => <span key={t} className="chip chip-ink text-[10px]">{t}</span>)}
              </div>
            </div>
          </button>
        </DialogTrigger>
        <button
          onClick={(e) => { e.stopPropagation(); toggle(c.slug); }}
          aria-label={saved ? "Remove bookmark" : "Save cocktail"}
          className={`absolute top-3 right-3 h-9 w-9 rounded-full grid place-items-center shadow-md transition-colors ${saved ? "bg-pink text-cream" : "bg-ink text-cream hover:bg-pink hover:text-cream"}`}
          data-testid={`bookmark-${c.slug}`}
        >
          <Heart size={16} fill={saved ? "currentColor" : "none"} />
        </button>
      </div>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-ink-panel border-cream/10 p-0 gap-0 block" data-testid={`cocktail-modal-${c.slug}`}>
        <div className="flex flex-col">
        <div className="aspect-[16/9] overflow-hidden bg-ink-2 flex-shrink-0">
          <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
        </div>
        <div className="p-6 sm:p-7">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="chip chip-pink">{c.base_spirit}</span>
            <span className="chip chip-ink">{c.strength}</span>
            <span className="chip chip-ink">ABV {c.abv}</span>
            <span className="flex items-center gap-0.5 text-pink-brand text-xs font-semibold ml-auto" title="Editor's Rating">
              <Star size={12} fill="currentColor" /> {c.beginner_rating}/5 Editor's Pick
            </span>
          </div>
          <div className="mt-3 flex items-start justify-between gap-3">
            <h2 className="font-serif-display text-3xl font-bold text-cream">{c.name}</h2>
            <button
              onClick={() => printCocktail(c)}
              className="flex-shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-full border border-cream/20 text-cream text-xs font-semibold uppercase tracking-widest hover:bg-ink hover:text-cream transition-colors"
              data-testid={`print-recipe-${c.slug}`}
              aria-label="Print recipe"
            >
              <Printer size={14} /> Print
            </button>
          </div>
          <p className="mt-2 text-cream/70 font-body">{c.description}</p>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div>
              <p className="eyebrow">Ingredients</p>
              <ul className="mt-2 space-y-1 font-body text-cream/85 text-sm">
                {c.ingredients.map((i, k) => <li key={k}>· {i}</li>)}
              </ul>
              <p className="eyebrow mt-5">Best For</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {c.occasions.map(o => <span key={o} className="chip chip-ink text-[10px]">{o}</span>)}
              </div>
              <p className="eyebrow mt-5">Taste Profile</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {c.taste_profile.map(t => <span key={t} className="chip chip-pink text-[10px]">{t}</span>)}
              </div>
            </div>
            <div>
              <p className="eyebrow">Method</p>
              <ol className="mt-2 space-y-2 font-body text-cream/85 text-sm list-decimal list-inside">
                {c.method.map((m, k) => <li key={k}>{m}</li>)}
              </ol>
              <div className="hr-rule my-5" />
              <p className="font-serif-editor italic text-cream/85 text-sm">
                <span className="text-pink-brand not-italic font-semibold uppercase text-[10px] tracking-widest">Pro Tip · </span>{c.pro_tip}
              </p>
            </div>
          </div>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
